/**
 * @fileoverview Arquivo responsável por centralizar os estilos do construtor de fluxos.
 */

import { makeStyles } from "@material-ui/core/styles";

var useStyles = makeStyles(function (theme) {
  var isDark = theme.palette.type === "dark";
  var accent = "#5B7CFF";
  var textPrimary = isDark ? "#f0f2f5" : "#111318";
  var textSecondary = isDark ? "#a0aec0" : "#5a6478";
  var textFaint = isDark ? "#5a6478" : "#a0aec0";
  var border = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.07)";
  var surface = isDark ? "#0e1017" : "#f7f8fc";
  var panelBg = isDark ? "rgba(14, 16, 23, 0.95)" : "rgba(255, 255, 255, 0.97)";

  return {
    "@global": {
      "@keyframes pulse": {
        "0%": { transform: "scale(1)", opacity: 1 },
        "50%": { transform: "scale(1.4)", opacity: 0.6 },
        "100%": { transform: "scale(1)", opacity: 1 },
      },
    },
    root: {
      flex: 1,
      minHeight: 0,
      width: "100%",
      display: "flex",
      flexDirection: "column",
      background: surface,
    },

    topbar: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "0 16px",
      height: 52,
      borderBottom: "1px solid " + border,
      background: panelBg,
      flexShrink: 0,
      zIndex: 10,
    },
    backBtn: {
      color: textSecondary,
      padding: 6,
      "&:hover": { color: textPrimary },
    },
    divider: {
      width: 1,
      height: 24,
      background: border,
    },
    titleInput: {
      border: "none",
      background: "transparent",
      color: textPrimary,
      fontSize: 15,
      fontWeight: 700,
      outline: "none",
      flex: 1,
      minWidth: 0,
      padding: "4px 8px",
      borderRadius: 6,
      transition: "background 0.15s",
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
      },
      "&:focus": {
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
      },
    },
    topbarRight: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginLeft: "auto",
    },
    statusTag: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontSize: 11,
      fontWeight: 600,
      padding: "3px 10px",
      borderRadius: 6,
    },
    activeTag: {
      background: "rgba(34, 197, 94, 0.12)",
      color: "#22c55e",
    },
    inactiveTag: {
      background: isDark ? "rgba(100, 116, 139, 0.12)" : "rgba(100, 116, 139, 0.08)",
      color: textFaint,
    },
    saveBtn: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "7px 16px",
      borderRadius: 8,
      border: "none",
      background: accent,
      color: "#fff",
      fontSize: 12,
      fontWeight: 500,
      cursor: "pointer",
      transition: "all 0.15s",
      "&:hover": {
        background: "#4a6ae8",
      },
      "&:disabled": {
        opacity: 0.6,
        cursor: "default",
      },
    },
    savedBtn: {
      background: "#22c55e",
      "&:hover": {
        background: "#22c55e",
      },
    },

    body: {
      display: "flex",
      flex: 1,
      overflow: "hidden",
    },

    sidebar: {
      width: 220, // default — sobrescrito em runtime via style inline
      borderRight: "1px solid " + border,
      background: panelBg,
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
      zIndex: 5,
    },

    // Handle de redimensionamento (4px de área ativa, 1px de linha visível).
    // Em viewports pequenos, esconde — a sidebar fica no layout padrão.
    sidebarResizeHandle: {
      position: "relative",
      width: 4,
      flexShrink: 0,
      cursor: "col-resize",
      zIndex: 6,
      background: "transparent",
      transition: "background 160ms ease",
      userSelect: "none",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        bottom: 0,
        left: "50%",
        width: 1,
        transform: "translateX(-50%)",
        background: "transparent",
        transition: "background 160ms ease",
      },
      "&:hover::before, &:active::before": {
        background: isDark
          ? "rgba(91, 124, 255, 0.55)"
          : "rgba(61, 92, 253, 0.5)",
        width: 2,
      },
      "@media (max-width: 767px)": {
        display: "none",
      },
    },
    sidebarTitle: {
      fontSize: 10,
      fontWeight: 700,
      color: textFaint,
      textTransform: "uppercase",
      letterSpacing: 1,
      padding: "16px 14px 8px",
    },
    sidebarList: {
      flex: 1,
      overflowY: "auto",
      padding: "0 8px 16px",
      "&::-webkit-scrollbar": { width: 4 },
      "&::-webkit-scrollbar-thumb": {
        background: "rgba(91, 124, 255, 0.2)",
        borderRadius: 2,
      },
    },
    nodeType: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "9px 10px",
      borderRadius: 10,
      cursor: "grab",
      transition: "all 0.15s",
      marginBottom: 2,
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
      },
      "&:active": {
        cursor: "grabbing",
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
      },
    },
    nodeTypeIcon: {
      width: 30,
      height: 30,
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    nodeTypeInfo: {
      flex: 1,
      minWidth: 0,
    },
    nodeTypeLabel: {
      fontSize: 12,
      fontWeight: 600,
      color: textPrimary,
      lineHeight: 1.2,
    },
    nodeTypeDesc: {
      fontSize: 10,
      color: textFaint,
      lineHeight: 1.3,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    nodeTypeGrip: {
      color: textFaint,
      opacity: 0.4,
      flexShrink: 0,
    },

    canvas: {
      flex: 1,
      position: "relative",
      "& .react-flow__node": {
        borderRadius: 12,
        border: "1px solid " + border,
        background: panelBg,
        color: textPrimary,
        boxShadow: isDark
          ? "0 2px 12px rgba(0,0,0,0.3)"
          : "0 2px 12px rgba(0,0,0,0.06)",
        fontSize: 12,
        padding: 0,
        overflow: "visible",
        transition: "box-shadow 0.2s, border-color 0.2s",
      },
      "& .react-flow__node.selected, & .react-flow__node:focus": {
        borderColor: accent + " !important",
        boxShadow: isDark
          ? "0 0 0 2px " + accent + "33, 0 4px 20px rgba(0,0,0,0.3) !important"
          : "0 0 0 2px " + accent + "22, 0 4px 20px rgba(0,0,0,0.08) !important",
      },
      "& .react-flow__edge-path": {
        stroke: isDark ? "rgba(91, 124, 255, 0.4)" : "rgba(91, 124, 255, 0.3)",
        strokeWidth: 2,
      },
      "& .react-flow__handle": {
        width: 8,
        height: 8,
        border: "2px solid " + accent,
        background: isDark ? "#0e1017" : "#fff",
        transition: "all 0.15s",
      },
      "& .react-flow__handle:hover": {
        background: accent,
        width: 10,
        height: 10,
      },
      "& .react-flow__controls": {
        borderRadius: 10,
        border: "1px solid " + border,
        overflow: "hidden",
        boxShadow: isDark
          ? "0 4px 16px rgba(0,0,0,0.3)"
          : "0 4px 16px rgba(0,0,0,0.08)",
        // Respeita a altura do logPanel (setada via CSS var no .canvas wrapper)
        bottom: "var(--log-panel-offset, 10px) !important",
      },
      "& .react-flow__controls-button": {
        background: panelBg,
        border: "none",
        borderBottom: "1px solid " + border,
        color: isDark ? "#f0f2f5" : textSecondary,
        fill: isDark ? "#f0f2f5" : textSecondary,
        width: 28,
        height: 28,
        "& svg": {
          fill: isDark ? "#f0f2f5" : textSecondary,
        },
        "&:hover": {
          background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
        },
      },
      "& .react-flow__minimap": {
        borderRadius: 10,
        border: "1px solid " + border,
        overflow: "hidden",
        boxShadow: isDark
          ? "0 4px 16px rgba(0,0,0,0.3)"
          : "0 4px 16px rgba(0,0,0,0.08)",
        bottom: "var(--log-panel-offset, 10px) !important",
        "& svg": {
          background: isDark ? "#0e1017" : "#f7f8fc",
        },
      },
      "& .react-flow__attribution": {
        display: "none",
      },
    },

    /* ── config panel (right) ──────────────────────── */
    configPanel: {
      width: 320,
      borderLeft: "1px solid " + border,
      background: panelBg,
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
      zIndex: 5,
      overflow: "hidden",
    },
    configHeader: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "14px 16px",
      borderBottom: "1px solid " + border,
      flexShrink: 0,
    },
    configHeaderIcon: {
      width: 32,
      height: 32,
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    configHeaderTitle: {
      fontSize: 14,
      fontWeight: 700,
      color: textPrimary,
      flex: 1,
    },
    configHeaderDesc: {
      fontSize: 11,
      color: textFaint,
    },
    configClose: {
      padding: 4,
      color: textFaint,
      "&:hover": { color: textPrimary },
    },
    configBody: {
      flex: 1,
      overflowY: "auto",
      padding: "16px 16px 48px",
      "&::-webkit-scrollbar": { width: 4 },
      "&::-webkit-scrollbar-thumb": {
        background: "rgba(91, 124, 255, 0.2)",
        borderRadius: 2,
      },
    },
    configField: {
      marginBottom: 16,
    },
    configLabelRow: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      marginBottom: 6,
    },
    configLabel: {
      fontSize: 12,
      fontWeight: 600,
      color: textSecondary,
    },
    configInfoIcon: {
      color: textFaint,
      cursor: "help",
    },
    configInfoTooltip: {
      fontSize: 11,
      lineHeight: 1.4,
      maxWidth: 220,
      color: textSecondary,
      background: isDark ? "#1a1c26" : "#fff",
      border: "1px solid " + border,
      borderRadius: 8,
      padding: "8px 10px",
      boxShadow: isDark
        ? "0 4px 16px rgba(0,0,0,0.4)"
        : "0 4px 16px rgba(0,0,0,0.1)",
    },
    configSelect: {
      width: "100%",
      minHeight: 38,
      padding: "0",
      borderRadius: 8,
      border: "1px solid " + border,
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
      color: textPrimary,
      fontSize: 13,
      outline: "none",
      cursor: "pointer",
      transition: "border-color 0.2s",
      "&:focus": {
        borderColor: accent,
      },
      "& option": {
        background: isDark ? "#14161e" : "#fff",
        color: textPrimary,
        padding: "8px",
      },
    },
    configChips: {
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
      marginTop: 6,
    },
    configChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontSize: 11,
      fontWeight: 600,
      padding: "4px 8px",
      borderRadius: 6,
      background: isDark ? "rgba(91, 124, 255, 0.12)" : "rgba(91, 124, 255, 0.08)",
      color: accent,
      cursor: "default",
    },
    configChipRemove: {
      cursor: "pointer",
      opacity: 0.6,
      "&:hover": { opacity: 1 },
    },
    configDropdown: {
      width: "100%",
      borderRadius: 8,
      border: "1px solid " + border,
      background: isDark ? "#14161e" : "#fff",
      maxHeight: 180,
      overflowY: "auto",
      "&::-webkit-scrollbar": { width: 4 },
      "&::-webkit-scrollbar-thumb": {
        background: "rgba(91, 124, 255, 0.2)",
        borderRadius: 2,
      },
    },
    configDropdownItem: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 12px",
      fontSize: 12,
      color: textPrimary,
      cursor: "pointer",
      transition: "background 0.1s",
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
      },
    },
    configDropdownCheck: {
      width: 16,
      height: 16,
      borderRadius: 4,
      border: "2px solid " + border,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    configDropdownCheckActive: {
      background: accent,
      borderColor: accent,
    },
    configDropdownColor: {
      width: 10,
      height: 10,
      borderRadius: "50%",
      flexShrink: 0,
    },
    configEmpty: {
      fontSize: 12,
      color: textFaint,
      padding: "12px 0",
      textAlign: "center",
    },

    configTextarea: {
      width: "100%",
      minHeight: 140,
      padding: "10px 12px",
      borderRadius: 8,
      border: "1px solid " + border,
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
      color: textPrimary,
      fontSize: 12,
      fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
      lineHeight: 1.6,
      resize: "vertical",
      outline: "none",
      transition: "border-color 0.2s",
      "&:focus": {
        borderColor: accent,
      },
      "&::placeholder": {
        color: textFaint,
      },
      "&::-webkit-scrollbar": { width: 4 },
      "&::-webkit-scrollbar-thumb": {
        background: "rgba(91, 124, 255, 0.2)",
        borderRadius: 2,
      },
    },

    configTextareaWrap: {
      position: "relative",
    },
    configExpandBtn: {
      position: "absolute",
      top: 8,
      right: 8,
      width: 26,
      height: 26,
      borderRadius: 6,
      border: "1px solid " + border,
      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
      color: textFaint,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "all 0.15s",
      zIndex: 2,
      "&:hover": {
        color: accent,
        borderColor: accent,
        background: accent + "12",
      },
    },

    promptModal: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1500,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(0, 0, 0, 0.6)",
    },
    promptModalContent: {
      width: "90vw",
      maxWidth: 720,
      height: "80vh",
      maxHeight: 600,
      background: isDark ? "#0e1017" : "#fff",
      borderRadius: 16,
      border: "1px solid " + border,
      display: "flex",
      flexDirection: "column",
      boxShadow: isDark
        ? "0 24px 64px rgba(0,0,0,0.6)"
        : "0 24px 64px rgba(0,0,0,0.15)",
      overflow: "hidden",
    },
    promptModalHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "14px 20px",
      borderBottom: "1px solid " + border,
      flexShrink: 0,
    },
    promptModalTitle: {
      fontSize: 14,
      fontWeight: 700,
      color: textPrimary,
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    promptModalClose: {
      padding: 4,
      color: textFaint,
      cursor: "pointer",
      borderRadius: 6,
      transition: "all 0.15s",
      "&:hover": { color: textPrimary, background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" },
    },
    promptModalBody: {
      flex: 1,
      display: "flex",
      overflow: "hidden",
    },
    promptModalSidebar: {
      width: 200,
      borderRight: "1px solid " + border,
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
      overflow: "hidden",
    },
    promptModalSidebarTitle: {
      fontSize: 10,
      fontWeight: 700,
      color: textFaint,
      textTransform: "uppercase",
      letterSpacing: 0.8,
      padding: "14px 14px 8px",
      flexShrink: 0,
    },
    promptModalSidebarHint: {
      fontSize: 10,
      color: textFaint,
      padding: "0 14px 10px",
      lineHeight: 1.4,
      flexShrink: 0,
    },
    promptModalSidebarList: {
      flex: 1,
      overflowY: "auto",
      padding: "0 8px 12px",
      "&::-webkit-scrollbar": { width: 3 },
      "&::-webkit-scrollbar-thumb": {
        background: "rgba(91, 124, 255, 0.2)",
        borderRadius: 2,
      },
    },
    promptModalFileItem: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 10px",
      borderRadius: 8,
      cursor: "grab",
      fontSize: 11,
      fontWeight: 600,
      color: textPrimary,
      transition: "all 0.15s",
      marginBottom: 2,
      "&:hover": {
        background: isDark ? "rgba(245, 158, 11, 0.08)" : "rgba(245, 158, 11, 0.06)",
      },
      "&:active": {
        cursor: "grabbing",
        background: isDark ? "rgba(245, 158, 11, 0.12)" : "rgba(245, 158, 11, 0.1)",
      },
    },
    promptModalFileIcon: {
      width: 24,
      height: 24,
      borderRadius: 6,
      background: "rgba(245, 158, 11, 0.12)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    promptModalFileName: {
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    promptModalFileTag: {
      fontSize: 9,
      fontWeight: 700,
      color: "#f59e0b",
      background: "rgba(245, 158, 11, 0.12)",
      padding: "2px 6px",
      borderRadius: 4,
      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
      whiteSpace: "nowrap",
    },
    promptModalEmptySidebar: {
      fontSize: 11,
      color: textFaint,
      padding: "16px 14px",
      textAlign: "center",
      lineHeight: 1.4,
    },
    promptModalTextareaWrap: {
      flex: 1,
      padding: 20,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    promptModalTextarea: {
      flex: 1,
      width: "100%",
      padding: "14px 16px",
      borderRadius: 10,
      border: "1px solid " + border,
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
      color: textPrimary,
      fontSize: 13,
      fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
      lineHeight: 1.7,
      resize: "none",
      outline: "none",
      transition: "border-color 0.2s",
      "&:focus": {
        borderColor: accent,
      },
      "&::placeholder": {
        color: textFaint,
      },
      "&::-webkit-scrollbar": { width: 6 },
      "&::-webkit-scrollbar-thumb": {
        background: "rgba(91, 124, 255, 0.25)",
        borderRadius: 3,
      },
    },

    configSliderRow: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    configSlider: {
      flex: 1,
      appearance: "none",
      height: 4,
      borderRadius: 2,
      background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
      outline: "none",
      cursor: "pointer",
      "&::-webkit-slider-thumb": {
        appearance: "none",
        width: 16,
        height: 16,
        borderRadius: "50%",
        background: accent,
        cursor: "pointer",
        border: "2px solid " + (isDark ? "#0e1017" : "#fff"),
        boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
      },
      "&::-moz-range-thumb": {
        width: 16,
        height: 16,
        borderRadius: "50%",
        background: accent,
        cursor: "pointer",
        border: "2px solid " + (isDark ? "#0e1017" : "#fff"),
        boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
      },
    },
    configSliderValue: {
      fontSize: 13,
      fontWeight: 700,
      color: accent,
      minWidth: 28,
      textAlign: "right",
    },

    configToggleRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "6px 0",
    },
    configToggleLabel: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12,
      fontWeight: 600,
      color: textPrimary,
    },

    configOptionGroup: {
      display: "flex",
      gap: 6,
      flexWrap: "wrap",
    },
    configOptionBtn: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 4,
      padding: "10px 8px",
      borderRadius: 8,
      border: "1px solid " + border,
      background: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
      color: textSecondary,
      fontSize: 11,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.15s",
      textAlign: "center",
      lineHeight: 1.3,
      "&:hover": {
        borderColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
      },
    },
    configOptionBtnActive: {
      borderColor: accent,
      background: accent + "12",
      color: accent,
      "&:hover": {
        borderColor: accent,
        background: accent + "18",
      },
    },
    configOptionIcon: {
      fontSize: 16,
      lineHeight: 1,
    },
    configOptionDesc: {
      fontSize: 9,
      fontWeight: 400,
      opacity: 0.7,
      lineHeight: 1.2,
    },

    configNumberInput: {
      width: "100%",
      padding: "8px 12px",
      borderRadius: 8,
      border: "1px solid " + border,
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
      color: textPrimary,
      fontSize: 13,
      fontWeight: 600,
      outline: "none",
      textAlign: "center",
      transition: "border-color 0.2s",
      "&:focus": {
        borderColor: accent,
      },
      "&::placeholder": {
        color: textFaint,
        fontWeight: 400,
      },
      "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
        appearance: "none",
        margin: 0,
      },
      appearance: "textfield",
    },
    configNumberUnit: {
      fontSize: 11,
      color: textFaint,
      marginTop: 4,
      textAlign: "center",
    },

    configInputRow: {
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    configInput: {
      flex: 1,
      padding: "8px 12px",
      borderRadius: 8,
      border: "1px solid " + border,
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
      color: textPrimary,
      fontSize: 12,
      outline: "none",
      transition: "border-color 0.2s",
      "&:focus": {
        borderColor: accent,
      },
      "&::placeholder": {
        color: textFaint,
      },
    },
    configAddBtn: {
      width: 32,
      height: 32,
      borderRadius: 8,
      border: "1px solid " + border,
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
      color: accent,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      flexShrink: 0,
      transition: "all 0.15s",
      "&:hover": {
        background: accent + "18",
        borderColor: accent,
      },
    },

    configDomainList: {
      display: "flex",
      flexDirection: "column",
      gap: 4,
      marginTop: 8,
    },
    configDomainItem: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "6px 10px",
      borderRadius: 8,
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
      border: "1px solid " + border,
    },
    configDomainIcon: {
      color: textFaint,
      flexShrink: 0,
    },
    configDomainText: {
      flex: 1,
      fontSize: 12,
      fontWeight: 500,
      color: textPrimary,
      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    configDomainRemove: {
      color: textFaint,
      cursor: "pointer",
      flexShrink: 0,
      padding: 2,
      borderRadius: 4,
      transition: "all 0.15s",
      "&:hover": {
        color: "#ef4444",
        background: "rgba(239, 68, 68, 0.1)",
      },
    },

    configUploadZone: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      padding: "24px 16px",
      borderRadius: 10,
      border: "2px dashed " + border,
      background: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
      cursor: "pointer",
      transition: "all 0.2s",
      "&:hover": {
        borderColor: "#f59e0b",
        background: isDark ? "rgba(245,158,11,0.04)" : "rgba(245,158,11,0.03)",
      },
    },
    configUploadZoneDrag: {
      borderColor: "#f59e0b",
      background: isDark ? "rgba(245,158,11,0.08)" : "rgba(245,158,11,0.06)",
    },
    configUploadIcon: {
      width: 40,
      height: 40,
      borderRadius: 10,
      background: "rgba(245, 158, 11, 0.12)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    configUploadText: {
      fontSize: 12,
      fontWeight: 600,
      color: textSecondary,
      textAlign: "center",
    },
    configUploadHint: {
      fontSize: 10,
      color: textFaint,
      textAlign: "center",
    },
    configFileList: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
      marginTop: 10,
    },
    configFileItem: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 10px",
      borderRadius: 8,
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
      border: "1px solid " + border,
    },
    configFileIcon: {
      width: 28,
      height: 28,
      borderRadius: 6,
      background: "rgba(245, 158, 11, 0.12)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    configFileInfo: {
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
    },
    configFileName: {
      fontSize: 12,
      fontWeight: 600,
      color: textPrimary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    configFileSize: {
      fontSize: 10,
      color: textFaint,
    },
    configFileRemove: {
      color: textFaint,
      cursor: "pointer",
      flexShrink: 0,
      padding: 2,
      borderRadius: 4,
      transition: "all 0.15s",
      "&:hover": {
        color: "#ef4444",
        background: "rgba(239, 68, 68, 0.1)",
      },
    },
    configUploading: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 10px",
      borderRadius: 8,
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
      border: "1px solid " + border,
      fontSize: 12,
      color: textSecondary,
    },

    toolsPopup: {
      position: "absolute",
      top: "100%",
      left: "50%",
      transform: "translateX(-50%)",
      marginTop: 6,
      background: panelBg,
      border: "1px solid " + border,
      borderRadius: 10,
      padding: 6,
      minWidth: 180,
      boxShadow: isDark
        ? "0 8px 24px rgba(0,0,0,0.5)"
        : "0 8px 24px rgba(0,0,0,0.12)",
      zIndex: 50,
    },
    toolsPopupItem: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 10px",
      borderRadius: 8,
      cursor: "pointer",
      fontSize: 12,
      fontWeight: 500,
      color: textPrimary,
      transition: "background 0.1s",
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
      },
    },
    toolsPopupIcon: {
      width: 24,
      height: 24,
      borderRadius: 6,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },

    loadingOverlay: {
      position: "absolute",
      top: 0, left: 0, right: 0, bottom: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: isDark ? "rgba(10,10,10,0.8)" : "rgba(247,248,252,0.8)",
      zIndex: 20,
    },

    /* ─── Log Panel ─────────────────────────── */
    logPanel: {
      position: "absolute",
      bottom: 0,
      left: 280,
      right: 0,
      background: panelBg,
      borderTop: "1px solid " + border,
      zIndex: 15,
      display: "flex",
      flexDirection: "column",
      transition: "height 0.25s ease",
      overflow: "hidden",
    },
    logPanelHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "8px 16px",
      borderBottom: "1px solid " + border,
      cursor: "pointer",
      userSelect: "none",
      "&:hover": { background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)" },
    },
    logPanelTitle: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 13,
      fontWeight: 600,
      color: textPrimary,
    },
    logPanelBadge: {
      background: accent,
      color: "#fff",
      borderRadius: 10,
      padding: "1px 8px",
      fontSize: 11,
      fontWeight: 600,
    },
    logPanelBody: {
      flex: 1,
      overflowY: "auto",
      padding: "8px 0",
    },
    logEntry: {
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      padding: "6px 16px",
      fontSize: 12,
      color: textSecondary,
      borderBottom: "1px solid " + (isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)"),
      "&:hover": { background: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)" },
    },
    logEntryIcon: {
      marginTop: 2,
      flexShrink: 0,
    },
    logEntryTime: {
      color: textFaint,
      fontFamily: "monospace",
      fontSize: 11,
      flexShrink: 0,
      minWidth: 65,
    },
    logEntryNode: {
      fontWeight: 600,
      color: accent,
    },
    logEntryStatus: {
      fontWeight: 600,
    },
    logStatusRunning: { color: "#f59e0b" },
    logStatusCompleted: { color: "#22c55e" },
    logStatusError: { color: "#ef4444" },
    logEmpty: {
      padding: "24px 16px",
      textAlign: "center",
      color: textFaint,
      fontSize: 13,
    },
  };
});

export default useStyles;
